<template>
  <div class="tier-progress-widget">
    <div class="tier-progress-header">
      <div class="tier-current">
        <TierBadge
          :tier-key="currentTier.key"
          :tier-name="currentTier.name"
          :tier-icon="currentTier.icon"
          size="medium"
        />
        <span class="tier-discount">{{ currentTier.discount_percentage }}% korting</span>
      </div>
      <div v-if="nextTier" class="tier-next">
        <span class="tier-next-label">Volgende tier</span>
        <TierBadge
          :tier-key="nextTier.key"
          :tier-name="nextTier.name"
          :tier-icon="nextTier.icon"
          size="small"
        />
      </div>
    </div>

    <div v-if="progress.next_tier" class="tier-progress-bar">
      <div class="progress-info">
        <span class="progress-text">{{ totalSpentFormatted }} uitgegeven</span>
        <span v-if="progress.amount_to_next" class="progress-remaining">
          Nog {{ formatCents(progress.amount_to_next) }} tot {{ nextTier?.name }}
        </span>
      </div>
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{ width: `${progressPercentage}%` }"
          :class="`progress-fill-${currentTier.key}`"
        >
          <span class="progress-percentage">{{ Math.round(progressPercentage) }}%</span>
        </div>
      </div>
    </div>

    <div v-else class="tier-max-reached">
      <span class="trophy-icon">🏆</span>
      <span class="max-tier-text">Je hebt het hoogste tier bereikt!</span>
    </div>

    <div v-if="showBenefits" class="tier-benefits">
      <h4 class="benefits-title">Jouw voordelen</h4>
      <ul class="benefits-list">
        <li class="benefit-item">
          <span class="benefit-icon">💰</span>
          <span>{{ currentTier.discount_percentage }}% korting op elke boeking</span>
        </li>
        <li class="benefit-item">
          <span class="benefit-icon">🔄</span>
          <span>Annuleren tot {{ Math.floor(currentTier.benefits.cancellation_hours / 24) }} dagen voor start</span>
        </li>
        <li v-if="currentTier.benefits.refund_policy === 'always_full'" class="benefit-item benefit-highlight">
          <span class="benefit-icon">✨</span>
          <span>Altijd 100% terugbetaling</span>
        </li>
        <li v-if="currentTier.benefits.custom_time_slots" class="benefit-item">
          <span class="benefit-icon">⏰</span>
          <span>Flexibel boeken: kies zelf je start- en einduur</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import TierBadge from './TierBadge.vue'
import type { TierConfig, UserTierInfo } from '@/composables/useLoyalty'

interface Props {
  currentTier: TierConfig
  progress: UserTierInfo['progress']
  nextTier?: TierConfig | null
  totalSpentFormatted: string
  showBenefits?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showBenefits: true
})

const progressPercentage = computed(() => props.progress.progress_percentage || 0)

function formatCents(cents: number): string {
  return '€' + (cents / 100).toFixed(2).replace('.', ',')
}
</script>

<style scoped>
.tier-progress-widget {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tier-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.tier-current {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.tier-discount {
  font-size: 0.875rem;
  font-weight: 600;
  color: #059669;
  background: #d1fae5;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
}

.tier-next {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.tier-next-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tier-progress-bar {
  margin-bottom: 1.5rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.progress-text {
  font-weight: 600;
  color: #1f2937;
}

.progress-remaining {
  color: #6b7280;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 2rem;
  background: #f3f4f6;
  border-radius: 1rem;
  overflow: hidden;
}

.progress-fill {
  position: relative;
  height: 100%;
  transition: width 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.75rem;
  border-radius: 1rem;
}

.progress-fill-bronze {
  background: linear-gradient(90deg, #cd7f32 0%, #b86f28 100%);
}

.progress-fill-silver {
  background: linear-gradient(90deg, #c0c0c0 0%, #a8a8a8 100%);
}

.progress-fill-gold {
  background: linear-gradient(90deg, #ffd700 0%, #ffb700 100%);
}

.progress-fill-platinum {
  background: linear-gradient(90deg, #e5e4e2 0%, #b9f2ff 100%);
}

.progress-percentage {
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.tier-max-reached {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.trophy-icon {
  font-size: 2rem;
}

.max-tier-text {
  font-size: 1rem;
  font-weight: 600;
  color: #92400e;
}

.tier-benefits {
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
}

.benefits-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #4b5563;
}

.benefit-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.benefit-highlight {
  color: #059669;
  font-weight: 600;
}

@media (max-width: 768px) {
  .tier-progress-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .tier-next {
    align-items: flex-start;
  }

  .progress-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
</style>
